import { useState, type KeyboardEvent } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const TAMANHO_JANELA = 7;
const OPCOES_ITENS_POR_PAGINA = [12, 24, 48, 96];

interface ListPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (pagina: number) => void;
  /** Quando presentes, renderiza o seletor de itens por página junto da paginação. */
  pageSize?: number;
  onPageSizeChange?: (tamanho: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

function paginasVisiveis(currentPage: number, totalPages: number): number[] {
  const inicio = Math.max(
    1,
    Math.min(totalPages - TAMANHO_JANELA + 1, currentPage - 3),
  );
  const fim = Math.min(totalPages, inicio + TAMANHO_JANELA - 1);
  const paginas: number[] = [];
  for (let pagina = inicio; pagina <= fim; pagina++) paginas.push(pagina);
  return paginas;
}

export function ListPagination({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = OPCOES_ITENS_POR_PAGINA,
  className,
}: ListPaginationProps) {
  const [paginaDigitada, setPaginaDigitada] = useState("");

  const mostrarSeletorTamanho = pageSize !== undefined && !!onPageSizeChange;
  const mostrarNavegacao = totalPages > 1;
  if (!mostrarNavegacao && !mostrarSeletorTamanho) return null;

  const irParaPagina = () => {
    const pagina = parseInt(paginaDigitada, 10);
    if (!Number.isFinite(pagina)) return;
    onPageChange(Math.min(totalPages, Math.max(1, pagina)));
    setPaginaDigitada("");
  };

  const handleInputKeyDown = (evento: KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === "Enter") {
      evento.preventDefault();
      irParaPagina();
    }
  };

  const janela = paginasVisiveis(currentPage, totalPages);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-4 gap-y-2",
        className,
      )}
    >
      {mostrarNavegacao && (
      <Pagination className="mx-0 w-auto">
        <PaginationContent>
          <PaginationItem>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Primeira página"
              disabled={currentPage === 1}
              onClick={() => onPageChange(1)}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          </PaginationItem>
          <PaginationItem>
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Anterior</span>
            </Button>
          </PaginationItem>
          {janela[0] > 1 && (
            <PaginationItem className="hidden sm:block">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          {janela.map((pagina) => (
            <PaginationItem key={pagina} className="hidden sm:block">
              <PaginationLink
                isActive={pagina === currentPage}
                onClick={() => onPageChange(pagina)}
                className="cursor-pointer"
              >
                {pagina}
              </PaginationLink>
            </PaginationItem>
          ))}
          {janela[janela.length - 1] < totalPages && (
            <PaginationItem className="hidden sm:block">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          <PaginationItem>
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              <span className="hidden sm:inline">Próxima</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </PaginationItem>
          <PaginationItem>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Última página"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      )}

      {mostrarNavegacao && (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          Página {currentPage} de {totalPages}
        </span>
      )}

      {mostrarNavegacao && (
        <div className="flex items-center gap-1.5">
          <label
            htmlFor="ir-para-pagina"
            className="text-sm text-muted-foreground whitespace-nowrap"
          >
            Ir para
          </label>
          <Input
            id="ir-para-pagina"
            type="number"
            min={1}
            max={totalPages}
            placeholder={`1-${totalPages}`}
            value={paginaDigitada}
            onChange={(evento) => setPaginaDigitada(evento.target.value)}
            onKeyDown={handleInputKeyDown}
            className="h-8 w-16 text-center"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={paginaDigitada.trim() === ""}
            onClick={irParaPagina}
          >
            Ir
          </Button>
        </div>
      )}

      {mostrarSeletorTamanho && (
        <Select
          value={pageSize.toString()}
          onValueChange={(valor) => onPageSizeChange(parseInt(valor, 10))}
        >
          <SelectTrigger className="h-8 w-[92px]" aria-label="Itens por página">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((opcao) => (
              <SelectItem key={opcao} value={opcao.toString()}>
                {opcao}/pg
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export default ListPagination;
